import React from 'react';
import { Play, Target, BarChart3, Zap, Sparkles, Brain, TrendingUp, Shield } from 'lucide-react';

const Features = () => {
  const features = [
    {
      icon: <Play className="w-6 h-6" />,
      title: "Multi-Platform",
      description: "Supports Meta, TikTok, Instagram, YouTube, and Twitter",
      color: "from-indigo-500 to-purple-500",
      bgColor: "from-indigo-50 to-purple-50",
      borderColor: "border-indigo-200"
    },
    {
      icon: <Brain className="w-6 h-6" />,
      title: "AI-Powered Insights",
      description: "Advanced analysis using Gemini AI technology",
      color: "from-purple-500 to-pink-500",
      bgColor: "from-purple-50 to-pink-50",
      borderColor: "border-purple-200"
    },
    {
      icon: <BarChart3 className="w-6 h-6" />,
      title: "Detailed Scoring",
      description: "Comprehensive scoring across multiple criteria",
      color: "from-pink-500 to-indigo-500",
      bgColor: "from-pink-50 to-indigo-50",
      borderColor: "border-pink-200"
    },
    {
      icon: <Target className="w-6 h-6" />,
      title: "Actionable",
      description: "Get specific tips to improve your ad performance",
      color: "from-indigo-600 to-purple-600",
      bgColor: "from-indigo-50 to-purple-50",
      borderColor: "border-indigo-200"
    }
  ];

  return (
    <section className="py-10 bg-gradient-to-br from-gray-50 to-indigo-50/30">
      <div className="max-w-6xl mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            Why Choose Our AI Analysis?
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto text-base">
            Experience the power of advanced AI technology combined with proven marketing frameworks to transform your video ad performance.
          </p>
        </div>

        {/* Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {features.map((feature, index) => (
            <div 
              key={index} 
              className="group relative"
            >
              {/* Background Effect */}
              <div className={`absolute inset-0 bg-gradient-to-br ${feature.bgColor} rounded-xl transform group-hover:scale-105 transition-all duration-300 opacity-0 group-hover:opacity-100`}></div>
              
              {/* Main Card */}
              <div className={`relative bg-white rounded-xl p-5 text-center border ${feature.borderColor} shadow-lg hover:shadow-2xl transition-all duration-300 group-hover:-translate-y-1 h-48 flex flex-col justify-between`}>
                {/* Top Section - Icon and Title */}
                <div className="flex-1">
                  <div className="flex justify-center mb-3">
                    <div className={`flex items-center justify-center w-12 h-12 bg-gradient-to-r ${feature.color} rounded-xl shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                      <div className="text-white">
                        {feature.icon}
                      </div>
                    </div>
                  </div>
                  
                  <h3 className="font-bold text-gray-900 mb-2 text-base group-hover:text-gray-800 transition-colors">
                    {feature.title}
                  </h3>
                </div>
                
                {/* Bottom Section - Description */}
                <div className="flex-shrink-0">
                  <p className="text-gray-600 text-sm leading-snug group-hover:text-gray-700 transition-colors">
                    {feature.description}
                  </p>
                </div>
                
                {/* Decorative Elements */}
                <div className="absolute top-3 right-3 w-1.5 h-1.5 bg-gradient-to-r from-indigo-400 to-purple-400 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                <div className="absolute bottom-3 left-3 w-1 h-1 bg-gradient-to-r from-purple-400 to-pink-400 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              </div>
            </div>
          ))}
        </div>

        {/* Call to Action Section */}
        <div className="mt-10">
          <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 rounded-xl p-8 text-center shadow-2xl relative overflow-hidden">
            {/* Background decorative elements */}
            <div className="absolute inset-0 bg-black opacity-10"></div>
            <div className="absolute top-3 left-3 w-1.5 h-1.5 bg-white bg-opacity-30 rounded-full"></div>
            <div className="absolute top-6 right-6 w-1 h-1 bg-white bg-opacity-40 rounded-full"></div>
            <div className="absolute bottom-4 left-8 w-1 h-1 bg-white bg-opacity-30 rounded-full"></div>
            <div className="absolute bottom-3 right-3 w-1.5 h-1.5 bg-white bg-opacity-20 rounded-full"></div>
            
            <div className="relative z-10">
              <h3 className="text-2xl font-bold text-white mb-3">
                Ready to Transform Your Video Performance?
              </h3>
              <p className="text-indigo-100 mb-6 max-w-2xl mx-auto text-base">
                Get instant AI-powered insights, storytelling guidance, and conversion optimization tips for your social media videos.
              </p>
              <button 
                onClick={() => {
                  // Scroll to the upload form at the top of the page
                  const uploadSection = document.querySelector('[data-upload-section]');
                  if (uploadSection) {
                    uploadSection.scrollIntoView({ behavior: 'smooth' });
                  } else {
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }
                }}
                className="inline-flex items-center justify-center px-6 py-3 bg-white text-indigo-600 font-bold text-base rounded-lg shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 hover:bg-indigo-50"
              >
                <Play className="w-5 h-5 mr-2" />
                Upload Your Video
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Features;
